import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AuthService } from '@insolution/auth-data-access';
import { ToastService } from '@insolution/core-ui';
import {
  Availability,
  AvailabilityChangedEvent,
  GetAvailabilityByIdGQL,
  OnAvailabilityChangedDocument,
} from '@insolution/graphql-data-access';
import { Apollo } from 'apollo-angular';
import { EMPTY, filter, map, Observable, Subject, switchMap, takeUntil } from 'rxjs';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';

@Injectable({
  providedIn: 'root',
})
export class AvailabilityService {
  private getAvailabilityByIdGQL = inject(GetAvailabilityByIdGQL);
  private toastService = inject(ToastService);
  private apollo = inject(Apollo);
  private authService = inject(AuthService);
  private setAvailabilityChangedSubscriptionIsCalled$ = new Subject<void>();
  private router = inject(Router);

  constructor() {
    this.setAvailabilityChangedSubscription();
  }

  public async getAvailability(availabilityId: 'Manager' | 'Workshop'): Promise<Availability | null> {
    try {
      const result = await firstValueFrom(this.getAvailabilityByIdGQL.fetch({ id: availabilityId }));

      if (result.errors && result.errors.length > 0) {
        this.toastService.showGeneralErrorToast(result.errors[0].message || 'Fetching availability failed');
        throw new Error(result.errors[0].message || 'Fetching availability failed');
      }

      return result.data?.getAvailabilityById || null;
    } catch (error) {
      this.toastService.showGeneralErrorToast(error instanceof Error ? error.message : undefined);
      console.error('Error fetching availability by ID:', error);
      throw error;
    }
  }

  public watchAvailability$(availabilityId: 'Manager' | 'Workshop'): Observable<Availability | null> {
    return this.getAvailabilityByIdGQL.watch({ id: availabilityId }).valueChanges.pipe(
      map((result) => {
        if (result.errors && result.errors.length > 0) {
          this.toastService.showGeneralErrorToast(result.errors[0].message || 'Fetching availability failed');
          throw new Error(result.errors[0].message || 'Fetching availability failed');
        }

        return result.data?.getAvailabilityById || null;
      })
    );
  }

  private availabilityChanged$(): Observable<AvailabilityChangedEvent> {
    return this.apollo
      .subscribe({
        query: OnAvailabilityChangedDocument,
      })
      .pipe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        filter((result) => !!(result.data as any)?.onAvailabilityChanged),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        map((result) => (result.data as any).onAvailabilityChanged)
      );
  }

  private setAvailabilityChangedSubscription(): void {
    this.setAvailabilityChangedSubscriptionIsCalled$.next();

    toObservable(this.authService.isLoggedIn)
      .pipe(
        takeUntil(this.setAvailabilityChangedSubscriptionIsCalled$),
        takeUntilDestroyed(),
        switchMap((isLoggedIn) => (isLoggedIn ? this.availabilityChanged$() : EMPTY))
      )
      .subscribe(() => {
        // flush apollo graphql cache
        this.apollo.client.cache.modify({
          fields: {
            getAvailabilityById(existing, { DELETE }) {
              return DELETE;
            },
          },
        });

        // invoke router navigation to trigger guards for current route
        setTimeout(() => {
          this.router.navigateByUrl(this.router.url, {
            onSameUrlNavigation: 'reload',
          });
        });
      });
  }
}
